import { useParams } from 'react-router-dom';
import { useState } from 'react';
import Back from 'components/Back/index.tsx';
import CopyOptionsForm from 'components/CopyIteration/CopyOptionsForm.tsx';
import useCampaign from 'hooks/useCampaign.ts';
import IterationPageHeader from 'components/IterationPageHeader/index.tsx';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useCreateIteration from 'queries/mutate/createIteration.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import { ApiError } from 'schemas/ApiError.schema';
import { API_CREATE_ITERATION_ERROR } from 'constants/errorMessages';
import { IIterationLevelParams } from 'utils/parameterUtils';
import { Iteration, IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';

export default function CopyIteration() {
  const { iterationId, teamId, campaignId } = useParams() as IIterationLevelParams;
  const { campaign } = useCampaign();
  const { navigateToCampaign } = useCampaignNavigate();
  const createIteration = useCreateIteration();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useProtectRoute();

  const thisIteration = campaign.CampaignConfig.Iterations?.find(
    (iteration: { ID?: string; IterationCohorts?: unknown[] }) => iteration.ID === iterationId,
  );

  const handleSuccessfulSubmission = async (newIteration: Iteration) => {
    try {
      setIsSubmitting(true);
      const result = await createIteration.mutateAsync({
        teamId,
        campaignId,
        iteration: newIteration as IterationDetailsFormValues,
      });
      const newIterationId = result.iteration_details?.ID ?? newIteration.ID!;
      await fetchSpecificCampaign.refetch();
      navigateToCampaign(`iteration/${newIterationId}/summary`);
    } catch {
      setError({ message: API_CREATE_ITERATION_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Iteration summary" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`} />
      </div>

      {thisIteration && (
        <>
          <IterationPageHeader iteration={thisIteration} title="Choose how to use the copied iteration" />
          <CopyOptionsForm
            iteration={thisIteration}
            onSuccessfulSubmission={handleSuccessfulSubmission}
            isPending={isSubmitting}
          />
        </>
      )}
      <ApiErrorDetails apiError={error ?? createIteration.error} />
    </>
  );
}
